import React from 'react';
import SEO from '../components/SEO';
import { Link } from 'react-router-dom';
import { getCityFromHost } from '../utils/getCityFromHost';

const AboutUs = () => {
  const city = getCityFromHost();
  const cityName = city ? city.charAt(0).toUpperCase() + city.slice(1) : 'Your City';

  return (
    <>
      <SEO
        title="About Us"
        description="Learn about Digital Fatehabad - your trusted local business directory in Fatehabad. Connecting businesses with customers. Verified listings, real reviews, easy search."
        canonical="/about"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {/* Hero Section */}
        <div className="text-center mb-12 md:mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            About Digital Fatehabad
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Connecting local businesses with customers in {cityName} and beyond
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
          {/* Who We Are */}
          <div className="lg:col-span-2 space-y-8">
            <section className="card p-6 md:p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Who We Are</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Digital Fatehabad is a comprehensive online platform dedicated to helping local businesses thrive and making it easier for customers to discover the best services and products in Fatehabad. We believe in the power of local commerce and are committed to building a strong, connected business community.
                </p>
                <p>
                  Our platform serves as a bridge between businesses and customers, providing a user-friendly interface where businesses can showcase their offerings, and customers can easily find exactly what they're looking for. Whether you're searching for a restaurant, a plumber, a doctor, or any other service, we've got you covered.
                </p>
                <p>
                  We understand that every business is unique, and that's why we've created a flexible platform that allows businesses to highlight their specialties, share their story, and connect with their target audience effectively.
                </p>
              </div>
            </section>

            {/* Our Mission */}
            <section className="card p-6 md:p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Our mission is to empower local businesses by providing them with a powerful digital presence and helping them reach more customers. We strive to:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Make it easy for customers to discover and connect with local businesses</li>
                  <li>Provide businesses with tools to showcase their products and services effectively</li>
                  <li>Build a trusted platform where customers can find reliable reviews and ratings</li>
                  <li>Support the growth of the local economy by promoting local commerce</li>
                  <li>Create a seamless experience for both businesses and customers</li>
                </ul>
              </div>
            </section>

            {/* Why Choose Us */}
            <section className="card p-6 md:p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Why Choose Us</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Verified Listings</h3>
                    <p className="text-sm text-gray-600">All businesses are verified to ensure authenticity and reliability</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">User Reviews</h3>
                    <p className="text-sm text-gray-600">Real reviews from real customers to help you make informed decisions</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Easy Search</h3>
                    <p className="text-sm text-gray-600">Find exactly what you're looking for with our advanced search and filters</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Mobile Friendly</h3>
                    <p className="text-sm text-gray-600">Access our directory from any device, anywhere, anytime</p>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            <div className="card p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Links</h3>
              <div className="space-y-2">
                <Link to="/submit-business" className="block text-blue-600 hover:text-blue-700 font-medium">
                  Submit Your Business
                </Link>
                <Link to="/contact" className="block text-blue-600 hover:text-blue-700 font-medium">
                  Contact Us
                </Link>
                <Link to="/faq" className="block text-blue-600 hover:text-blue-700 font-medium">
                  Frequently Asked Questions
                </Link>
              </div>
            </div>

            <div className="card p-6 bg-gradient-to-br from-blue-50 to-blue-100">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Get Started</h3>
              <p className="text-gray-700 mb-4 text-sm">
                Ready to grow your business? List your business on our directory today and reach more customers.
              </p>
              <Link
                to="/submit-business"
                className="inline-block w-full text-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
              >
                List Your Business
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AboutUs;

